import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import styles from './Dashboard.module.css';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faUserCheck, faMapMarkedAlt, faDollarSign, faChartBar,
  faFilter, faUndo, faHeartPulse, faUserTie
} from '@fortawesome/free-solid-svg-icons';

const Dashboard = () => {
  const [afiliados, setAfiliados] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [afiliadosFiltrados, setAfiliadosFiltrados] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [cuadres, setCuadres] = useState([]);
  const [dependencias, setDependencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  
  const [filtros, setFiltros] = useState({
    sector: '',
    estado: 'todos',
    fechaDesde: '',
    fechaHasta: '',
    edadDesde: '',
    edadHasta: '',
    estadoCivil: '',
    sexo: '',
    exonerado: 'todos',
    diaPago: '',
    cobrador: ''
  });

  const COLORS = ['#003366', '#0055aa', '#0077dd', '#3399ff', '#66bbff', '#99ddff', '#aa00aa', '#ff6600'];

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [afiliadosList, sectoresList, pagosList] = await Promise.all([
        api.getAfiliados?.() || fetch('/api/affiliates.php').then(r => r.json()),
        api.getSectores?.() || fetch('/api/sectors.php').then(r => r.json()),
        api.getPayments?.() || fetch('/api/payments.php').then(r => r.json()).catch(() => [])
      ]);
      
      setAfiliados(Array.isArray(afiliadosList) ? afiliadosList : []);
      setSectores(Array.isArray(sectoresList) ? sectoresList : []);
      setAfiliadosFiltrados(Array.isArray(afiliadosList) ? afiliadosList : []);
      setPagos(Array.isArray(pagosList) ? pagosList : []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setAfiliados([]);
      setSectores([]);
      setPagos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...afiliados];

    if (filtros.sector) {
      filtered = filtered.filter(a => a.sector === filtros.sector);
    }

    if (filtros.estado !== 'todos') {
      const esActivo = filtros.estado === 'activo' ? 1 : 0;
      filtered = filtered.filter(a => parseInt(a.activo || 0) === esActivo);
    }

    if (filtros.exonerado !== 'todos') {
      const esExonerado = filtros.exonerado === 'si' ? 1 : 0;
      filtered = filtered.filter(a => parseInt(a.exonerado || 0) === esExonerado);
    }

    if (filtros.sexo) {
      filtered = filtered.filter(a => a.sexo === filtros.sexo);
    }

    if (filtros.estadoCivil) {
      filtered = filtered.filter(a => a.estadoCivil === filtros.estadoCivil);
    }

    if (filtros.edadDesde) {
      const edadMin = parseInt(filtros.edadDesde);
      filtered = filtered.filter(a => parseInt(a.edad || 0) >= edadMin);
    }

    if (filtros.edadHasta) {
      const edadMax = parseInt(filtros.edadHasta);
      filtered = filtered.filter(a => parseInt(a.edad || 0) <= edadMax);
    }

    if (filtros.fechaDesde) {
      const desde = new Date(filtros.fechaDesde);
      filtered = filtered.filter(a => {
        const fecha = a.fechaInicioAfiliacion ? new Date(a.fechaInicioAfiliacion) : new Date();
        return fecha >= desde;
      });
    }

    if (filtros.fechaHasta) {
      const hasta = new Date(filtros.fechaHasta);
      filtered = filtered.filter(a => {
        const fecha = a.fechaInicioAfiliacion ? new Date(a.fechaInicioAfiliacion) : new Date();
        return fecha <= hasta;
      });
    }

    setAfiliadosFiltrados(filtered);
  }, [filtros, afiliados]);

  const limpiarFiltros = () => {
    setFiltros({
      sector: '',
      estado: 'todos',
      fechaDesde: '',
      fechaHasta: '',
      edadDesde: '',
      edadHasta: '',
      estadoCivil: '',
      sexo: '',
      exonerado: 'todos',
      diaPago: '',
      cobrador: ''
    });
  };

  // Calcular estadísticas
  const calcularStats = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const edades = datos.map(a => parseInt(a.edad || 0)).filter(e => e > 0);
    
    return {
      total: datos.length,
      activos: datos.filter(a => parseInt(a.activo || 0) === 1).length,
      inactivos: datos.filter(a => parseInt(a.activo || 0) === 0).length,
      exonerados: datos.filter(a => parseInt(a.exonerado || 0) === 1).length,
      sectores: [...new Set(datos.map(a => a.sector))].length,
      edadPromedio: edades.length > 0 ? (edades.reduce((a, b) => a + b) / edades.length).toFixed(1) : 0,
      edadMaxima: edades.length > 0 ? Math.max(...edades) : 0,
      edadMinima: edades.length > 0 ? Math.min(...edades) : 0
    };
  };

  // Agrupar por sector
  const afiliadosPorSector = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const agrupadoMap = {};
    
    datos.forEach(a => {
      const sector = a.sector || 'Sin Sector';
      if (!agrupadoMap[sector]) {
        agrupadoMap[sector] = 0;
      }
      agrupadoMap[sector]++;
    });

    return Object.entries(agrupadoMap)
      .map(([nombre, count]) => ({ nombre, afiliados: count }))
      .sort((a, b) => b.afiliados - a.afiliados);
  };

  // Distribución por edad
  const distribucionPorEdad = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const rangos = {
      '18-25': 0,
      '26-35': 0,
      '36-45': 0,
      '46-55': 0,
      '56-65': 0,
      '66+': 0
    };

    datos.forEach(a => {
      const edad = parseInt(a.edad || 0);
      if (edad >= 18 && edad <= 25) rangos['18-25']++;
      else if (edad >= 26 && edad <= 35) rangos['26-35']++;
      else if (edad >= 36 && edad <= 45) rangos['36-45']++;
      else if (edad >= 46 && edad <= 55) rangos['46-55']++;
      else if (edad >= 56 && edad <= 65) rangos['56-65']++;
      else if (edad > 65) rangos['66+']++;
    });

    return Object.entries(rangos).map(([rango, count]) => ({
      rango,
      cantidad: count
    }));
  };

  // Distribución por sexo
  const distribucionPorSexo = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const sexos = {};
    
    datos.forEach(a => {
      const sexo = a.sexo || 'No especificado';
      sexos[sexo] = (sexos[sexo] || 0) + 1;
    });

    return Object.entries(sexos).map(([nombre, value]) => ({
      name: nombre === 'M' ? 'Masculino' : nombre === 'F' ? 'Femenino' : nombre,
      value: value,
      fill: nombre === 'M' ? '#0077dd' : nombre === 'F' ? '#ff66cc' : '#999999'
    }));
  };

  // Estado civil
  const distribucionPorEstadoCivil = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const estados = {};

    datos.forEach(a => {
      const estado = a.estadoCivil || 'No especificado';
      estados[estado] = (estados[estado] || 0) + 1;
    });

    return Object.entries(estados).map(([nombre, count]) => ({
      nombre,
      cantidad: count
    }));
  };

  // Afiliaciones por mes
  const afiliacionesPorMes = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const meses = {};

    datos.forEach(a => {
      if (a.fechaInicioAfiliacion) {
        const fecha = new Date(a.fechaInicioAfiliacion);
        const mes = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        meses[mes] = (meses[mes] || 0) + 1;
      }
    });

    return Object.entries(meses)
      .sort()
      .map(([mes, cantidad]) => ({
        mes,
        cantidad
      }))
      .slice(-12);
  };

  // Enfermedades crónicas
  const distribucionEnfermedades = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const conEnfermedades = datos.filter(a => a.enfermedadesCronicas && a.enfermedadesCronicas.trim() !== '').length;
    const sinEnfermedades = datos.length - conEnfermedades;

    return [
      { name: 'Con Enfermedades', value: conEnfermedades, fill: '#ff6b6b' },
      { name: 'Sin Enfermedades', value: sinEnfermedades, fill: '#51cf66' }
    ];
  };

  // Tipo de sangre
  const distribucionTipoSangre = () => {
    const datos = afiliadosFiltrados.length > 0 ? afiliadosFiltrados : afiliados;
    const tipos = {};

    datos.forEach(a => {
      const tipo = a.tipoSangre || 'No especificado';
      tipos[tipo] = (tipos[tipo] || 0) + 1;
    });

    return Object.entries(tipos).map(([nombre, count]) => ({
      nombre,
      cantidad: count
    }));
  };

  const estadisticasPagos = () => {
    let pagosFiltrados = pagos;

    // Filtrar por rango de fechas
    if (filtros.fechaDesde || filtros.fechaHasta) {
      pagosFiltrados = pagosFiltrados.filter(p => {
        const fechaPago = new Date(p.fecha || p.fecha_creacion);
        if (filtros.fechaDesde && fechaPago < new Date(filtros.fechaDesde)) return false;
        if (filtros.fechaHasta && fechaPago > new Date(filtros.fechaHasta)) return false;
        return true;
      });
    }

    // Filtrar por día específico
    if (filtros.diaPago) {
      pagosFiltrados = pagosFiltrados.filter(p => {
        const fechaPago = new Date(p.fecha || p.fecha_creacion).toISOString().split('T')[0];
        return fechaPago === filtros.diaPago;
      });
    }

    // Filtrar por cobrador
    if (filtros.cobrador) {
      pagosFiltrados = pagosFiltrados.filter(p => {
        const nombreCobrador = p.nombre_cobrador || '';
        return nombreCobrador.includes(filtros.cobrador);
      });
    }

    const totalPagos = pagosFiltrados.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
    const cantidadPagos = pagosFiltrados.length;
    
    return {
      totalRecaudado: totalPagos.toFixed(2),
      cantidadPagos: cantidadPagos,
      promedioPago: cantidadPagos > 0 ? (totalPagos / cantidadPagos).toFixed(2) : 0
    };
  };

  const distribucionMetodosPago = () => {
    const metodos = {};
    pagos.forEach(p => {
      const metodo = p.metodo_pago || 'Sin especificar';
      metodos[metodo] = (metodos[metodo] || 0) + 1;
    });

    return Object.entries(metodos).map(([nombre, count]) => ({
      name: nombre,
      value: count,
      fill: COLORS[Object.keys(metodos).indexOf(nombre) % COLORS.length]
    }));
  };

  const pagosPorMes = () => {
    const meses = {};
    pagos.forEach(p => {
      const fecha = new Date(p.fecha || p.fecha_creacion);
      const mes = fecha.toLocaleString('es-ES', { month: 'short', year: '2-digit' });
      meses[mes] = (meses[mes] || 0) + parseFloat(p.monto || 0);
    });

    return Object.entries(meses).slice(-12).map(([mes, monto]) => ({
      mes,
      monto: parseFloat(monto).toFixed(2)
    }));
  };

  const pagosPorSector = () => {
    const sectoresPagos = {};
    pagos.forEach(p => {
      const afiliado = afiliados.find(a => String(a.id) === String(p.idAfiliado));
      if (afiliado) {
        const sector = afiliado.sector || 'Sin Sector';
        if (!sectoresPagos[sector]) {
          sectoresPagos[sector] = { total: 0, cantidad: 0 };
        }
        sectoresPagos[sector].total += parseFloat(p.monto || 0);
        sectoresPagos[sector].cantidad += 1;
      }
    });

    return Object.entries(sectoresPagos)
      .map(([sector, data]) => ({
        nombre: sector,
        total: data.total.toFixed(2),
        cantidad: data.cantidad
      }))
      .sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
  };

  const rankingCobradores = () => {
    const cobradores = {};
    pagos.forEach(p => {
      const cobrador = p.idAfiliado || 'Sin asignar';
      const afiliado = afiliados.find(a => String(a.id) === String(p.idAfiliado));
      const nombre = afiliado ? `${afiliado.nombres} ${afiliado.apellidos}` : `Afiliado ${cobrador}`;
      
      if (!cobradores[cobrador]) {
        cobradores[cobrador] = { nombre, total: 0, cantidad: 0 };
      }
      cobradores[cobrador].total += parseFloat(p.monto || 0);
      cobradores[cobrador].cantidad += 1;
    });

    const totalGeneral = Object.values(cobradores).reduce((sum, c) => sum + c.total, 0);
    
    return Object.values(cobradores)
      .map(cobrador => ({
        ...cobrador,
        porcentaje: totalGeneral > 0 ? ((cobrador.total / totalGeneral) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.total - a.total);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const obtenerCobradores = () => {
    const cobradores = new Set();
    pagos.forEach(p => {
      if (p.nombre_cobrador && p.nombre_cobrador !== 'Sin asignar') {
        cobradores.add(p.nombre_cobrador);
      }
    });
    return Array.from(cobradores).sort();
  };

  const stats = calcularStats();
  const sectorData = afiliadosPorSector();
  const edadData = distribucionPorEdad();
  const sexoData = distribucionPorSexo();
  const estadoCivilData = distribucionPorEstadoCivil();
  const mesData = afiliacionesPorMes();
  const enfermData = distribucionEnfermedades();
  const sangreData = distribucionTipoSangre();

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinnerLoader}></div>
          <p className={styles.loadingText}>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h2 className={styles.titulo}>
          <FontAwesomeIcon icon={faChartBar} /> Panel de Control - Estadísticas
        </h2>
      </div>

      {/* SECCIÓN DE FILTROS */}
      <div className={styles.filtrosSection}>
        <h3 className={styles.filtrosTitulo}>
          <FontAwesomeIcon icon={faFilter} /> Filtros
        </h3>
        
        <div className={styles.filtrosGrid}>
          <div>
            <label>Sector:</label>
            <select 
              value={filtros.sector} 
              onChange={(e) => setFiltros({...filtros, sector: e.target.value})}
              className={styles.select}
            >
              <option value="">Todos</option>
              {sectores.map(s => (
                <option key={s.id} value={s.nombre_sector}>{s.nombre_sector}</option>
              ))}
            </select>
          </div>

          <div>
            <label>Estado:</label>
            <select 
              value={filtros.estado} 
              onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
              className={styles.select}
            >
              <option value="todos">Todos</option>
              <option value="activo">Activos</option>
              <option value="inactivo">Inactivos</option>
            </select>
          </div>

          <div>
            <label>Exonerado:</label>
            <select 
              value={filtros.exonerado} 
              onChange={(e) => setFiltros({...filtros, exonerado: e.target.value})}
              className={styles.select}
            >
              <option value="todos">Todos</option>
              <option value="si">Exonerados</option>
              <option value="no">No Exonerados</option>
            </select>
          </div>

          <div>
            <label>Sexo:</label>
            <select 
              value={filtros.sexo} 
              onChange={(e) => setFiltros({...filtros, sexo: e.target.value})}
              className={styles.select}
            >
              <option value="">Todos</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>

          <div>
            <label>Estado Civil:</label>
            <select 
              value={filtros.estadoCivil} 
              onChange={(e) => setFiltros({...filtros, estadoCivil: e.target.value})}
              className={styles.select}
            >
              <option value="">Todos</option>
              <option value="Soltero">Soltero</option>
              <option value="Casado">Casado</option>
              <option value="Divorciado">Divorciado</option>
              <option value="Viudo">Viudo</option>
            </select>
          </div>

          <div>
            <label>Edad Desde:</label>
            <input 
              type="number" 
              value={filtros.edadDesde}
              onChange={(e) => setFiltros({...filtros, edadDesde: e.target.value})}
              placeholder="Edad mínima"
              className={styles.input}
            />
          </div>

          <div>
            <label>Edad Hasta:</label>
            <input 
              type="number" 
              value={filtros.edadHasta}
              onChange={(e) => setFiltros({...filtros, edadHasta: e.target.value})}
              placeholder="Edad máxima"
              className={styles.input}
            />
          </div>

          <div>
            <label>Afiliación Desde:</label>
            <input 
              type="date" 
              value={filtros.fechaDesde}
              onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
              className={styles.input}
            />
          </div>

          <div>
            <label>Afiliación Hasta:</label>
            <input 
              type="date" 
              value={filtros.fechaHasta}
              onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
              className={styles.input}
            />
          </div>

          <div>
            <label>Día de Pago:</label>
            <input 
              type="date" 
              value={filtros.diaPago}
              onChange={(e) => setFiltros({...filtros, diaPago: e.target.value})}
              className={styles.input}
              title="Filtrar pagos por día específico"
            />
          </div>

          <div>
            <label>Cobrador:</label>
            <select 
              value={filtros.cobrador}
              onChange={(e) => setFiltros({...filtros, cobrador: e.target.value})}
              className={styles.input}
              title="Filtrar pagos por cobrador"
            >
              <option value="">Todos los cobradores</option>
              {obtenerCobradores().map(cobrador => (
                <option key={cobrador} value={cobrador}>
                  {cobrador}
                </option>
              ))}
            </select>
          </div>

          <button onClick={limpiarFiltros} className={styles.btnLimpiar}>
            <FontAwesomeIcon icon={faUndo} /> Limpiar
          </button>
        </div>
      </div>

      {/* TARJETAS DE ESTADÍSTICAS */}
      <div className={styles.statsGrid}>
        <StatCard icon={faUsers} label="Total Afiliados" value={stats.total} />
        <StatCard icon={faUserCheck} label="Activos" value={stats.activos} />
        <StatCard icon={faMapMarkedAlt} label="Sectores" value={stats.sectores} />
        <StatCard icon={faDollarSign} label="Inactivos" value={stats.inactivos} />
        <StatCard icon={faHeartPulse} label="Edad Promedio" value={stats.edadPromedio} />
      </div>

      {/* GRÁFICOS */}
      <div className={styles.chartsSection}>
        {/* Gráfico de Afiliados por Sector */}
        <div className={styles.chartCard}>
          <h3>Afiliados por Sector</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sectorData} margin={{top: 5, right: 10, left: 0, bottom: 60}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
              <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={60} tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 9}} width={30} />
              <Tooltip contentStyle={{fontSize: 10, backgroundColor: '#fff'}} />
              <Bar dataKey="afiliados" fill="#0077dd" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Distribución por Edad */}
        <div className={styles.chartCard}>
          <h3>Distribución por Rango de Edad</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={edadData} margin={{top: 5, right: 10, left: 0, bottom: 10}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
              <XAxis dataKey="rango" tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 9}} width={30} />
              <Tooltip contentStyle={{fontSize: 10, backgroundColor: '#fff'}} />
              <Bar dataKey="cantidad" fill="#0055aa" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico circular Sexo */}
        <div className={styles.chartCard}>
          <h3>Distribución por Sexo</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={sexoData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={45}
                fill="#8884d8"
                dataKey="value"
                labelProps={{fontSize: 8}}
              >
                {sexoData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip contentStyle={{fontSize: 9, backgroundColor: '#fff'}} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico circular Estado Civil */}
        <div className={styles.chartCard}>
          <h3>Distribución por Estado Civil</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={estadoCivilData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({nombre}) => nombre}
                outerRadius={45}
                fill="#8884d8"
                dataKey="cantidad"
                labelProps={{fontSize: 8}}
              >
                {estadoCivilData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{fontSize: 9, backgroundColor: '#fff'}} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Afiliaciones por Mes */}
        <div className={styles.chartCard}>
          <h3>Afiliaciones Últimos 12 Meses</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={mesData} margin={{top: 5, right: 10, left: 0, bottom: 60}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
              <XAxis dataKey="mes" angle={-45} textAnchor="end" height={60} tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 9}} width={30} />
              <Tooltip contentStyle={{fontSize: 10, backgroundColor: '#fff'}} />
              <Line type="monotone" dataKey="cantidad" stroke="#0077dd" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico circular Enfermedades */}
        <div className={styles.chartCard}>
          <h3>Afiliados con Enfermedades Crónicas</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={enfermData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={42}
                fill="#8884d8"
                dataKey="value"
                labelProps={{fontSize: 7, fill: '#333'}}
              >
                {enfermData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip contentStyle={{fontSize: 9, backgroundColor: '#fff'}} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla de Tipo de Sangre */}
        <div className={styles.chartCard}>
          <h3>Distribución por Tipo de Sangre</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Qty</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {sangreData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.nombre}</td>
                  <td>{item.cantidad}</td>
                  <td>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progress}
                        style={{
                          width: `${stats.total > 0 ? (item.cantidad / stats.total * 100) : 0}%`
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECCIÓN DE PAGOS Y ESTADÍSTICAS */}
      <div style={{marginTop: '20px'}}>
        {/* TARJETAS DE ESTADÍSTICAS DE PAGOS */}
        <div className={styles.statsGrid}>
          <StatCard icon={faDollarSign} label="Total Recaudado" value={`$${estadisticasPagos().totalRecaudado}`} />
          <StatCard icon={faChartBar} label="Cantidad Pagos" value={estadisticasPagos().cantidadPagos} />
          <StatCard icon={faUsers} label="Promedio Pago" value={`$${estadisticasPagos().promedioPago}`} />
        </div>

        {/* GRÁFICOS DE PAGOS */}
        <div className={styles.chartsSection}>
          {/* Métodos de Pago */}
          <div className={styles.chartCard}>
            <h3>Distribución por Método de Pago</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={distribucionMetodosPago()}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({name, value}) => `${name} ${value}`}
                  outerRadius={45}
                  fill="#8884d8"
                  dataKey="value"
                  labelProps={{fontSize: 8}}
                >
                  {distribucionMetodosPago().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{fontSize: 9, backgroundColor: '#fff'}} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Pagos por Mes */}
          <div className={styles.chartCard}>
            <h3>Pagos por Mes</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={pagosPorMes()} margin={{top: 5, right: 10, left: 0, bottom: 60}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                <XAxis dataKey="mes" angle={-45} textAnchor="end" height={60} tick={{fontSize: 9}} />
                <YAxis tick={{fontSize: 9}} width={40} />
                <Tooltip contentStyle={{fontSize: 10, backgroundColor: '#fff'}} />
                <Bar dataKey="monto" fill="#00aa00" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TABLA DETALLADA DE PAGOS */}
        <div className={styles.tableCard}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
            <h3 style={{margin: 0}}>Detalle de Pagos</h3>
            <button 
              onClick={() => toggleSection('pagos')}
              style={{
                background: '#0077dd',
                color: '#fff',
                border: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {expandedSections.pagos ? '▼ Ocultar' : '▶ Expandir'}
            </button>
          </div>
          
          {expandedSections.pagos && (
            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Afiliado</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Cobrador</th>
                    <th>Fecha</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.length > 0 ? (
                    pagos.slice(0, 50).map(pago => (
                      <tr key={pago.id}>
                        <td>{pago.id}</td>
                        <td>{pago.idAfiliado}</td>
                        <td>${parseFloat(pago.monto || 0).toFixed(2)}</td>
                        <td>{pago.metodo_pago}</td>
                        <td style={{color: '#0077dd', fontWeight: '600'}}>{pago.nombre_cobrador || 'Sin asignar'}</td>
                        <td>{pago.fecha || '-'}</td>
                        <td>{pago.hora_pago || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7" style={{textAlign: 'center', color: '#999'}}>No hay pagos registrados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* RANKING DE COBRADORES */}
      <div style={{marginTop: '20px'}}>
        <h3 style={{fontSize: '16px', color: '#003366', fontWeight: '700', marginBottom: '12px'}}>Ranking de Cobradores</h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px'}}>
          {rankingCobradores().slice(0, 10).map((cobrador, index) => (
            <div key={index} style={{
              background: index < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][index] : '#f5f5f5',
              padding: '14px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{fontSize: '28px', marginBottom: '6px'}}>
                {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
              </div>
              <p style={{margin: '0 0 8px 0', fontSize: '12px', fontWeight: '700', color: '#333'}}>{cobrador.nombre.substring(0, 20)}</p>
              <p style={{margin: '0 0 4px 0', fontSize: '14px', fontWeight: '800', color: '#003366'}}>${parseFloat(cobrador.total).toFixed(2)}</p>
              <p style={{margin: '0', fontSize: '11px', color: '#666'}}>Pagos: {cobrador.cantidad} ({cobrador.porcentaje}%)</p>
            </div>
          ))}
        </div>
      </div>

      {/* ESTADÍSTICAS DE SECTORES QUE MÁS HAN COBRADO */}
      <div className={styles.tableCard} style={{marginTop: '20px'}}>
        <h3>Sectores con Más Cobranzas</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Sector</th>
              <th>Total Cobrado</th>
              <th>Cantidad Pagos</th>
              <th>Promedio</th>
            </tr>
          </thead>
          <tbody>
            {pagosPorSector().map((sector, idx) => (
              <tr key={idx}>
                <td>{sector.nombre}</td>
                <td><strong>${sector.total}</strong></td>
                <td>{sector.cantidad}</td>
                <td>${(parseFloat(sector.total) / sector.cantidad).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* TABLA DE RESUMEN POR SECTOR */}
      <div className={styles.tableCard}>
        <h3>Resumen por Sector</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Sector</th>
              <th>Total Afiliados</th>
              <th>% del Total</th>
              <th>Barra de Progreso</th>
            </tr>
          </thead>
          <tbody>
            {sectorData.map((sector, idx) => (
              <tr key={idx}>
                <td>{sector.nombre}</td>
                <td><strong>{sector.afiliados}</strong></td>
                <td>{((sector.afiliados / stats.total) * 100).toFixed(1)}%</td>
                <td>
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progress}
                      style={{
                        width: `${(sector.afiliados / stats.total) * 100}%`,
                        backgroundColor: COLORS[idx % COLORS.length]
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* HISTORIAL FILTRADO DE PAGOS POR COBRADOR */}
      {filtros.cobrador && (
        <div className={styles.tableCard}>
          <h3>Historial de Pagos - {filtros.cobrador}</h3>
          <p style={{margin: '0 0 12px 0', color: '#666', fontSize: '12px'}}>
            Total recaudado por este cobrador: <strong>${estadisticasPagos().totalRecaudado}</strong> 
            ({estadisticasPagos().cantidadPagos} pagos)
          </p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Afiliado</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Fecha</th>
                <th>Hora</th>
              </tr>
            </thead>
            <tbody>
              {pagos.filter(p => p.nombre_cobrador === filtros.cobrador).length > 0 ? (
                pagos.filter(p => p.nombre_cobrador === filtros.cobrador).map(pago => (
                  <tr key={pago.id}>
                    <td>{pago.id}</td>
                    <td>{pago.idAfiliado}</td>
                    <td>${parseFloat(pago.monto || 0).toFixed(2)}</td>
                    <td>{pago.metodo_pago}</td>
                    <td>{pago.fecha || '-'}</td>
                    <td>{pago.hora_pago || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" style={{textAlign: 'center', color: '#999'}}>No hay pagos para este cobrador</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Componente de Tarjeta de Estadística
const StatCard = ({ icon, label, value }) => {
  return (
    <div className={`${styles.statCard} ${styles.enabled}`}>
      <div className={styles.iconContainer} style={{color: '#0077dd'}}>
        <FontAwesomeIcon icon={icon} size="lg" />
      </div>
      <div className={styles.content}>
        <p className={styles.label}>{label}</p>
        <p className={styles.value}>{value}</p>
      </div>
    </div>
  );
};

export default Dashboard;
